import { useOneSignal } from "@onesignal/onesignal-vue3";
import DeviceAPI from "../device";
import { loginFacebook } from "./facebook";
import { loginGoogle } from "./google";
import { loginApple } from "./apple";
import useRequest from "~/hooks/use-request";
import useSegment from "~/hooks/use-segment";
import useUserStore from "~/stores/user";
import { formatUserModel } from "~/utils/user";
import { formatDeviceModel, getCurrentDeviceInfo } from "~/services/format";
// TODO: Move auth module into a separate package.

interface IAuthSuccessParams {
  profile: IResponseProfile;
  token: string;
  tokenUpdateTime: number;
  tokenExpirationTime: number;
  refreshToken: string;
  devices: IResponseDevice[];
}

function onAuthSuccess(params: IAuthSuccessParams) {
  const { profile, token, tokenExpirationTime, tokenUpdateTime, refreshToken, devices } = params;

  const userStore = useUserStore();
  const oneSignal = useOneSignal();
  const segment = useSegment();
  const user = formatUserModel(profile);

  userStore.user = user;
  userStore.token = token; // TODO: Handling the users who are currently logged in. Will be removed in the future.
  userStore.userTokenData = { token, tokenExpirationTime, tokenUpdateTime, refreshToken };
  userStore.devices = devices.map(formatDeviceModel);
  oneSignal.sendTag("user_id", user.id);
  segment.identify(user.id, user);
}

const AuthAPI = {
  signup(payload: IRequestPayloadSignUp) {
    const userStore = useUserStore();
    const { request } = useRequest();
    const body = { ...payload, strategy: "refresh" };

    return request
      .post<IResponseSignUp>("/api/user/register", { body }).then((response) => {
        const currentTime = Math.floor(Date.now() / 1000);

        userStore.user = formatUserModel(response.content.profile);
        userStore.userTokenData = {
          token: response.content.tokenData.access_token,
          tokenExpirationTime: response.content.tokenData.expires_in,
          tokenUpdateTime: currentTime,
          refreshToken: response.content.tokenData.refresh_token,
        };
      })
      .then(() => AuthAPI.signin(payload.email, payload.password));
  },

  signin(username: string, password: string): Promise<void> {
    const { request } = useRequest();
    const device = getCurrentDeviceInfo();
    const payload: IRequestPayloadLogin = {
      username,
      password,
      device_id: device.deviceId,
      device_name: device.deviceName,
      strategy: "refresh",
    };

    return request.post<IContentResponse<IResponseLogin>>("/api/user/login", { body: payload }).then((response) => {
      const token = response.content.tokenData?.token;
      const currentTime = Math.floor(Date.now() / 1000);

      if (response.content.device_error) {
        const userStore = useUserStore();
        userStore.devices = response.content.devices.map(formatDeviceModel);

        const error: Error & { code?: string; token?: string; provider?: IAuthProvider } = Error("device_limit_exceeded");

        error.code = "device_limit_exceeded";
        error.token = token;
        error.provider = "credentials";

        throw error;
      }

      const authParams = {
        token,
        tokenExpirationTime: response.content.tokenData.expires_in,
        tokenUpdateTime: currentTime,
        profile: response.content.profile,
        refreshToken: response.content.tokenData.refresh_token,
        devices: response.content.devices,
      };

      onAuthSuccess(authParams);
    });
  },

  async signinWithProvider(provider: IAuthProvider): Promise<void> {
    // eslint-disable-next-line no-console
    console.log(`Signing in with '${provider}' provider...`);
    let providerResponse: ISocialProfile | null = null;

    if (provider === "facebook")
      providerResponse = await loginFacebook();
    else if (provider === "google")
      providerResponse = await loginGoogle();
    else if (provider === "apple")
      providerResponse = await loginApple();
    else
      throw new Error(`"${provider}" auth provider not supported!`);

    return AuthAPI.signSSO(providerResponse).then((response) => {
      const currentTime = Math.floor(Date.now() / 1000);

      if (response.auth_data.device_error) {
        const userStore = useUserStore();
        userStore.devices = response.auth_data.devices.map(formatDeviceModel);

        const error: Error & { code?: string; token?: string; provider?: IAuthProvider } = Error("device_limit_exceeded");

        error.code = "device_limit_exceeded";
        error.token = response.token;
        error.provider = provider;

        throw error;
      }

      const authParams = {
        profile: response.auth_data.profile,
        token: response.token,
        tokenExpirationTime: response.expires_in,
        tokenUpdateTime: currentTime,
        refreshToken: response.refresh_token,
        devices: response.auth_data.devices,
      };

      onAuthSuccess(authParams);
    });
  },

  signinWithFacebook(): Promise<string> {
    return Promise.resolve("");
  },

  signSSO(provider: ISocialProfile): Promise<IResponseSSOLogin> {
    const config = useRuntimeConfig().public;
    const { request } = useRequest();
    const currentDevice = DeviceAPI.current();
    const token_type = `urn:ietf:params:oauth:token-type:${provider.token_type}`;
    const payload = {
      grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
      subject_token_type: token_type,
      subject_issuer: provider.provider_name,
      requested_token_type: token_type,
      subject_token: provider.access_token || provider.id_token,
      device: {
        id: currentDevice.deviceId,
        name: currentDevice.deviceName,
        os_name: "",
        os_version: "",
        app_version: config.APP_VERSION,
      },
      profile: {
        first_name: provider.full_name.split(" ")[0],
        last_name: provider.full_name.split(" ")[1],
      },
      strategy: "refresh",
    };

    return request.post<IResponseSSOLogin>("/api/oauth2/token", { body: payload });
  },

  logout(authToken: string) {
    const { request } = useRequest();

    return request
      .post<IResponseSignUp>("/api/user/logout", { withToken: authToken });
  },

  refreshAccessToken(): Promise<void> {
    const userStore = useUserStore();
    const error: Error & { code?: string } = Error("expired_session_error");
    error.code = "refresh_token_failed";

    if (!userStore.userTokenData?.refreshToken)
      return Promise.reject(error);

    if (userStore.pendingAccessTokenRefresh === null) {
      const config = useRuntimeConfig().public;
      const { request } = useRequest();
      const currentDevice = DeviceAPI.current();

      const payload = {
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        subject_token_type: "urn:ietf:params:oauth:token-type:refresh_token",
        subject_issuer: "pixellot",
        requested_token_type: "urn:ietf:params:oauth:token-type:access_token",
        subject_token: userStore.userTokenData.refreshToken,
        device: {
          id: currentDevice.deviceId,
          name: currentDevice.deviceName,
          os_name: "",
          os_version: "",
          app_version: config.APP_VERSION,
        },
        strategy: "refresh",
      };

      userStore.pendingAccessTokenRefresh = request
        .post<IResponseSSOLogin>("/api/oauth2/token", { body: payload })
        .then((response) => {
          const currentTime = Math.floor(Date.now() / 1000);

          userStore.userTokenData = {
            token: response.token,
            tokenExpirationTime: response.expires_in,
            tokenUpdateTime: currentTime,
            refreshToken: response.refresh_token,
          };
        })
        .catch(() => Promise.reject(error))
        .finally(() => {
          userStore.pendingAccessTokenRefresh = null;
        });
    }

    return userStore.pendingAccessTokenRefresh;
  },
};

export default AuthAPI;
